import React, { useEffect } from 'react';
import { Status } from '../../@types';
import { mergeSx, Styles } from '@t2c/shared-fe';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import CustomizedProgressBars from '../../Common/ProgressBar';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { Iconify, NextImage } from '@sdf/base';
import { useAppContext } from '../../hooks';
import { CustomDialogContainer, CustomDialogTitle } from 'apps/t2c-fe/Common/dialog';
import { DialogContent } from '@mui/material';
import { useRouter } from 'next/router';
import { ProgressStuckNote } from '../../Common';

interface Props {
  textToSpeechId: string;
  shouldOpenDialog: boolean;
  handleCloseDialog: () => void;
}

export const VoiceoverProccessingModal: React.FC<Props> = ({
  textToSpeechId,
  shouldOpenDialog,
  handleCloseDialog,
}) => {
  const {
    websocket: { textToSpeechTaskMap },
  } = useAppContext();

  const { push } = useRouter();

  const taskData = textToSpeechTaskMap.get(textToSpeechId);

  const isTaskSuccessful = taskData?.status === Status.Success;
  const isTaskInProgress = taskData?.status === Status.InProgress;
  const isTaskFailed = taskData?.status === Status.Error;
  const overallProgressPercentage = taskData?.progress || 0;

  useEffect(() => {
    if (isTaskSuccessful) {
      handleCloseDialog();
      push(`text-to-audio/${textToSpeechId}`);
    }
  }, [isTaskSuccessful]);
  return (
    <CustomDialogContainer open={shouldOpenDialog} maxWidth="sm">
      <CustomDialogTitle title={'Generating Voiceover'} onClose={handleCloseDialog} />
      <DialogContent sx={styles.contentContainer}>
        {/* Upload/Task In Progress */}
        {(!taskData?.status || isTaskInProgress) && (
          <>
            <Typography sx={styles.heading}>{'Generating...'}</Typography>
            <CircularProgress />
            <Stack justifyContent="center" alignItems="center" width="100%" rowGap={0.5}>
              <CustomizedProgressBars value={overallProgressPercentage} sx={{ width: '80%' }} />
            </Stack>
            <Typography sx={styles.description}>
              {`Your text is being processed, which may take a few minutes. You can stay here to proceed
                when it's ready, or minimize this window and continue from the dashboard later.`}
            </Typography>
            <ProgressStuckNote />
          </>
        )}
        {/* Task Success */}
        {isTaskSuccessful && (
          <>
            <Typography sx={styles.heading}>{'Your Voice is Now Digital'}</Typography>
            <NextImage src="/assets/icons/VideoSuccessIcon.svg" height={130} width={130} alt="" />
            <Typography textAlign={'center'}>
              {`Your text has been successfully turned into audio. Proceed to the next screen or
                    minimize this window to review it from the dashboard.`}
            </Typography>
          </>
        )}
        {/* Task Error */}
        {isTaskFailed && (
          <>
            <Typography sx={styles.heading}>Oops!</Typography>
            <ErrorOutlineIcon sx={styles.errorIcon} color="error" />
            <Typography sx={styles.errorMessage}>
              {`We encountered an issue! Your audio couldn’t be created. Please try again or verify your settings.`}
            </Typography>
          </>
        )}
      </DialogContent>
      <Divider sx={{ width: '100%' }} />
      <Stack
        sx={mergeSx(styles.footerButtonContainer, {
          justifyContent: isTaskInProgress ? 'space-between' : 'flex-end',
        })}
      >
        {!taskData || isTaskFailed ? (
          <>
            <Button
              variant="outlined"
              fullWidth
              sx={styles.cancelButton}
              onClick={handleCloseDialog}
            >
              {isTaskFailed ? 'Close' : 'Minimize'}
            </Button>
          </>
        ) : (
          <>
            {isTaskInProgress && (
              <Button
                variant="contained"
                fullWidth
                sx={styles.actionButton}
                onClick={handleCloseDialog}
                disabled={taskData?.status === Status.NotStarted}
              >
                Minimize
              </Button>
            )}
          </>
        )}
      </Stack>
    </CustomDialogContainer>
  );
};

const styles: Styles = {
  actionButton: {
    py: 2.5,
    maxWidth: '150px',
    color: (theme) => theme.palette.text.primary,
    boxShadow: 'none',
  },
  cancelButton: {
    py: 2.5,
    maxWidth: '150px',
    color: (theme) => theme.palette.text.primary,
  },
  dialogContent: { maxHeight: 550 },
  contentContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 3,
    backgroundColor: (theme) => theme.palette.background.secondary,
    borderColor: (theme) => theme.palette.background.border,
  },
  heading: { fontSize: '26px', fontWeight: 600, textAlign: 'center' },
  description: { textAlign: 'center', color: (theme) => theme.palette.text.placeholder },
  errorIcon: { width: 75, height: 75 },
  errorMessage: { color: (theme) => theme.palette.text.error, textAlign: 'center' },
  footerButtonContainer: { flexDirection: 'row', px: 3, py: 2 },
};
